import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FormControlLabel, Switch } from '@mui/material';
import { deleteGsa, getAllGsas, getGsaList } from '../../../../api/endpoints/system';
import type { Gsa } from '../../../../api/types/system';
import { useAuthStore } from '../../../../auth/authStore';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  StatusChip,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../components/common';
import { GsaFormDialog } from '../GsaFormDialog';

/** Full CRUD: create + edit (`GsaFormDialog`), delete (`Gsa/DeleteGsa`), logo upload on edit. */
export default function GsaListPage() {
  const [formGsa, setFormGsa] = useState<Gsa | null | undefined>(undefined);
  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const isMasterGsa = useAuthStore((s) => !!s.currentUser?.isMasterGsa);

  const query = useQuery({
    queryKey: ['system', 'gsaList', showAll ? 'all' : 'own'],
    queryFn: () => (showAll ? getAllGsas() : getGsaList()),
  });
  const rows: Gsa[] = useMemo(() => query.data?.gsaList ?? [], [query.data]);

  const del = useDeleteResource<Gsa>({
    mutationFn: (gsa) => deleteGsa({ id: gsa.id, name: gsa.name }),
    invalidateKeys: [['system', 'gsaList']],
    successMessage: 'Firma silindi.',
    errorMessage: 'Silme başarısız.',
  });

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.name, row.gsaType, row.city, row.email, row.taxNo, row.isActive ? 'Aktif' : 'Pasif']
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const columns: DataTableColumn<Gsa>[] = [
    { id: 'name', header: 'Firma Adı', cell: (r) => r.name },
    { id: 'type', header: 'Tip', cell: (r) => r.gsaType ?? '—' },
    { id: 'city', header: 'Şehir', cell: (r) => r.city ?? '—' },
    { id: 'email', header: 'E-posta', cell: (r) => r.email ?? '—' },
    { id: 'taxNo', header: 'Vergi No', cell: (r) => r.taxNo ?? '—' },
    { id: 'status', header: 'Durum', cell: (r) => <StatusChip active={r.isActive} /> },
  ];

  return (
    <ListPageScaffold
      title="Firmalar (Acenteler)"
      createLabel="Yeni Firma"
      onCreate={() => {
        setFormGsa(null);
        setFormOpen(true);
      }}
      search={{ value: search, onChange: setSearch, placeholder: 'Firma, şehir, e-posta, vergi no...' }}
      toolbar={
        isMasterGsa ? (
          <FormControlLabel
            control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
            label="Tüm firmalar"
          />
        ) : undefined
      }
      query={query}
      rows={filteredRows}
      columns={columns}
      getRowKey={(r) => r.id}
      emptyTitle={rows.length === 0 ? 'Kayıtlı firma bulunamadı.' : 'Aramayla eşleşen firma bulunamadı.'}
      rowActions={(r) => (
        <RowActions
          onEdit={() => {
            setFormGsa(r);
            setFormOpen(true);
          }}
          onDelete={() => del.request(r)}
        />
      )}
    >
      <GsaFormDialog open={formOpen} gsa={formGsa} onClose={() => setFormOpen(false)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Firmayı sil"
        description={`${del.target?.name ?? ''} firmasını silmek istediğinize emin misiniz?`}
        confirmLabel="Sil"
      />
    </ListPageScaffold>
  );
}
